import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { History, RotateCcw, Trash2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { SnapshotMeta } from "./snapshots";

/**
 * History popover for the WS4 facilitation tools. Renders a grid of
 * snapshot tiles (thumbnail + relative timestamp + Manual/Auto badge).
 * Clicking a tile triggers `onRestore(id)`; the trash glyph triggers
 * `onRemove(id)` after a confirm.
 *
 * Visual chrome matches the legacy `#chatContextDropdown` /
 * `ReferencePicker` styling: bg-panel surface, true-blue 0.35 border,
 * soft cyan glow shadow.
 *
 * Keyboard navigation (additive on top of click + Radix's built-in
 * Escape-to-close + Tab cycling):
 *   - ←/→/↑/↓: move a 2px focus ring between tiles (wrap at edges; ↑/↓
 *     step by `COLS` rows). The ring is hidden until the first arrow
 *     press (`focusedIndex` starts at -1) so the popover opens quiet.
 *   - Enter: restore the focused snapshot (mirrors clicking the tile).
 *   - Delete / Backspace: delete the focused snapshot (mirrors trash).
 *     After delete, `focusedIndex` is clamped so it can't dangle past
 *     the end of the shrunken list.
 * `COLS` is hard-coded to 2 to match the grid template; if that ever
 * changes, this is a single-line tweak.
 */

/** Column count for arrow-key row stepping — mirrors the
 *  `gridTemplateColumns` on `TILE_GRID_STYLE`. */
const COLS = 2;

type Props = {
  snapshots: SnapshotMeta[];
  onRestore: (id: string) => void;
  onRemove: (id: string) => void;
  /** Trigger button — usually the History pill the tool renders. */
  children: ReactNode;
};

function relativeTime(ts: number): string {
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const seconds = Math.round(diff / 1000);
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

const POPOVER_STYLE: CSSProperties = {
  minWidth: 320,
  maxWidth: 380,
  background: "var(--bg-panel)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.35)",
  borderRadius: 10,
  overflow: "hidden",
  boxShadow:
    "0 0 30px rgba(var(--accent-true-blue-rgb), 0.12), 0 8px 24px rgba(0, 0, 0, 0.3)",
};

const HEADER_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 12px",
  borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.18)",
  fontSize: "var(--fs-label)",
  fontWeight: 700,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "var(--accent-sky)",
};

const EMPTY_STYLE: CSSProperties = {
  padding: "20px 14px",
  textAlign: "center",
  fontSize: "var(--fs-label)",
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

const TILE_GRID_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  padding: 10,
  maxHeight: 360,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor:
    "rgba(var(--accent-true-blue-rgb), 0.4) rgba(255, 255, 255, 0.03)",
};

const TILE_BASE_STYLE: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: 6,
  borderRadius: 8,
  background: "var(--bg-card)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
  cursor: "pointer",
  textAlign: "left",
  transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
};

const THUMB_STYLE: CSSProperties = {
  width: "100%",
  height: 80,
  objectFit: "cover",
  borderRadius: 6,
  background: "var(--bg-panel-alt)",
  display: "block",
};

const BADGE_BASE_STYLE: CSSProperties = {
  display: "inline-block",
  padding: "1px 6px",
  borderRadius: 6,
  fontSize: "var(--fs-micro)",
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: "uppercase",
  lineHeight: 1.4,
};

const TRASH_BTN_STYLE: CSSProperties = {
  position: "absolute",
  top: 4,
  right: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: 6,
  background: "rgba(0, 0, 0, 0.45)",
  border: "1px solid rgba(var(--accent-melon-rgb), 0.45)",
  color: "var(--accent-melon)",
  cursor: "pointer",
  padding: 0,
  opacity: 0,
  transition: "opacity 0.15s, background 0.15s",
};

export function SnapshotHistoryPopover({
  snapshots,
  onRestore,
  onRemove,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  // -1 = no ring shown. First arrow press lands on index 0 (see
  // handlers below — `i < 0 ? 0 : …`).
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Reset the focus ring whenever the popover closes so the next open
  // starts quiet (matches the "no ring on open" expectation).
  useEffect(() => {
    if (!open) setFocusedIndex(-1);
  }, [open]);

  // Clamp the focus ring if the underlying list shrinks (delete) — keeps
  // the ring on a valid tile instead of pointing past the end.
  useEffect(() => {
    if (focusedIndex >= snapshots.length) {
      setFocusedIndex(snapshots.length === 0 ? -1 : snapshots.length - 1);
    }
  }, [snapshots.length, focusedIndex]);

  // Keyboard nav — bound to window while the popover is open. Escape is
  // handled by Radix Popover; Tab keeps its default action-button cycle.
  // We ignore keystrokes originating from inputs/textareas so future
  // search fields inside the popover wouldn't be hijacked.
  useEffect(() => {
    if (!open) return;
    const n = snapshots.length;
    if (n === 0) return;
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          setFocusedIndex((i) => (i < 0 ? 0 : (i - 1 + n) % n));
          return;
        case "ArrowRight":
          e.preventDefault();
          setFocusedIndex((i) => (i < 0 ? 0 : (i + 1) % n));
          return;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((i) => (i < 0 ? 0 : (i - COLS + n) % n));
          return;
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((i) => (i < 0 ? 0 : (i + COLS) % n));
          return;
        case "Enter":
          if (focusedIndex >= 0 && focusedIndex < n) {
            e.preventDefault();
            onRestore(snapshots[focusedIndex]!.id);
            setOpen(false);
          }
          return;
        case "Delete":
        case "Backspace":
          if (focusedIndex >= 0 && focusedIndex < n) {
            e.preventDefault();
            onRemove(snapshots[focusedIndex]!.id);
            // Clamp here too — the snapshots prop won't update until
            // the parent re-renders, so the effect above also runs;
            // this just keeps the ring snappy on the same key press.
            setFocusedIndex((i) =>
              i >= n - 1 ? Math.max(0, n - 2) : i,
            );
          }
          return;
        default:
          return;
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, snapshots, focusedIndex, onRestore, onRemove]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-auto p-0"
        style={POPOVER_STYLE}
      >
        <div style={HEADER_STYLE}>
          <History
            aria-hidden
            strokeWidth={1.8}
            style={{ width: 12, height: 12 }}
          />
          <span>History</span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: "var(--fs-micro)",
              color: "var(--text-muted)",
              letterSpacing: 1,
            }}
          >
            {snapshots.length}
          </span>
        </div>
        {snapshots.length === 0 ? (
          <div style={EMPTY_STYLE}>No snapshots yet</div>
        ) : (
          <div style={TILE_GRID_STYLE}>
            {snapshots.map((m, idx) => {
              const isManual = m.source === "manual";
              const badgeColor = isManual
                ? "var(--accent-gold)"
                : "var(--accent-sky)";
              const badgeRgb = isManual
                ? "var(--accent-gold-rgb)"
                : "var(--accent-sky-rgb)";
              const isFocused = idx === focusedIndex;
              return (
                <div
                  key={m.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Restore snapshot from ${relativeTime(m.createdAt)}`}
                  onClick={() => {
                    onRestore(m.id);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRestore(m.id);
                      setOpen(false);
                    }
                  }}
                  style={
                    isFocused
                      ? {
                          ...TILE_BASE_STYLE,
                          outline: "2px solid var(--accent-true-blue)",
                          outlineOffset: "2px",
                        }
                      : TILE_BASE_STYLE
                  }
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "var(--accent-true-blue)";
                    e.currentTarget.style.background =
                      "rgba(var(--accent-true-blue-rgb), 0.06)";
                    const trash = e.currentTarget.querySelector<HTMLButtonElement>(
                      "button[data-role='snapshot-trash']",
                    );
                    if (trash) trash.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(var(--accent-true-blue-rgb), 0.20)";
                    e.currentTarget.style.background = "var(--bg-card)";
                    const trash = e.currentTarget.querySelector<HTMLButtonElement>(
                      "button[data-role='snapshot-trash']",
                    );
                    if (trash) trash.style.opacity = "0";
                  }}
                >
                  {m.thumbnailDataUrl ? (
                    <img
                      src={m.thumbnailDataUrl}
                      alt=""
                      style={THUMB_STYLE}
                    />
                  ) : (
                    <div
                      style={{
                        ...THUMB_STYLE,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-muted)",
                        fontSize: "var(--fs-micro)",
                        letterSpacing: 1,
                      }}
                    >
                      No preview
                    </div>
                  )}
                  <button
                    type="button"
                    data-role="snapshot-trash"
                    aria-label="Delete snapshot"
                    title="Delete snapshot"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(m.id);
                    }}
                    style={TRASH_BTN_STYLE}
                  >
                    <Trash2
                      aria-hidden
                      strokeWidth={1.8}
                      style={{ width: 12, height: 12 }}
                    />
                  </button>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--fs-micro)",
                        color: "var(--text-muted)",
                        letterSpacing: 0.5,
                      }}
                    >
                      {relativeTime(m.createdAt)}
                    </span>
                    <span
                      style={{
                        ...BADGE_BASE_STYLE,
                        background: `rgba(${badgeRgb}, 0.14)`,
                        border: `1px solid rgba(${badgeRgb}, 0.40)`,
                        color: badgeColor,
                      }}
                    >
                      {isManual ? "Manual" : "Auto"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 12px",
            borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.18)",
            fontSize: "var(--fs-micro)",
            color: "var(--text-muted)",
            letterSpacing: 0.5,
          }}
        >
          <RotateCcw
            aria-hidden
            strokeWidth={1.8}
            style={{ width: 10, height: 10 }}
          />
          <span>Click a tile to restore</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
